
import React, { useState } from "react";
import { FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckboxGroup } from "@/components/ui/CheckboxGroup";
import { useLanguage } from "@/context/LanguageContext";

export const LocationFields = ({ form }: { form: any }) => {
  const { language } = useLanguage();

  const housingTypeOptions = [
    { value: 'basement', label: language === 'en' ? 'Basement' : '地下室' },
    { value: 'house', label: language === 'en' ? 'House' : '獨立房屋' },
    { value: 'room', label: language === 'en' ? 'Room' : '房間' },
    { value: 'condo', label: language === 'en' ? 'Condo' : '公寓' },
    { value: 'apartment', label: language === 'en' ? 'Apartment' : '住宅' },
    { value: 'semi-detached', label: language === 'en' ? 'Semi-detached' : '半獨立房屋' },
  ];

  const roomOptions = [
    { value: '0', label: '0' },
    ...Array.from({ length: 8 }, (_, i) => ({
      value: (i + 1).toString(),
      label: (i + 1).toString()
    }))
  ];

  return (
    <>
      <FormField
        control={form.control}
        name="housing_type"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-lg font-semibold">
              {language === 'en' ? 'Housing Type' : '房屋類型'}
              <span className="text-red-500 ml-1">*</span>
            </FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder={language === 'en' ? "Select a type" : "選擇房屋類型"} />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {housingTypeOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <FormField
          control={form.control}
          name="bedrooms"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-base font-medium">
                {language === 'en' ? 'Bedrooms' : '臥室數量'}
                <span className="text-red-500 ml-1">*</span>
              </FormLabel>
              <Select onValueChange={(value) => field.onChange(parseInt(value))} value={field.value?.toString()}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={language === 'en' ? "Select" : "選擇"} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {roomOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="living_rooms"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-base font-medium">
                {language === 'en' ? 'Living Rooms' : '客廳數量'}
                <span className="text-red-500 ml-1">*</span>
              </FormLabel>
              <Select onValueChange={(value) => field.onChange(parseInt(value))} value={field.value?.toString()}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={language === 'en' ? "Select" : "選擇"} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {roomOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="bathrooms"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-base font-medium">
                {language === 'en' ? 'Bathrooms' : '浴室數量'}
                <span className="text-red-500 ml-1">*</span>
              </FormLabel>
              <Select onValueChange={(value) => field.onChange(parseInt(value))} value={field.value?.toString()}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={language === 'en' ? "Select" : "選擇"} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {roomOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </>
  );
};

export const RentFields = ({ form }: { form: any }) => {
  const { language } = useLanguage();
  const [rentError, setRentError] = useState("");
  const [depositError, setDepositError] = useState("");

  const validateNumber = (value: string, setError: (error: string) => void) => {
    const numberRegex = /^\d+(\.\d+)?$/;
    if (value && !numberRegex.test(value)) {
      setError(language === 'en' ? "Only numbers are allowed" : "僅能輸入純數字");
      return false;
    } else {
      setError("");
      return true;
    }
  };

  const utilitiesOptions = [
    { id: 'water', label: language === 'en' ? 'Water' : '水費' },
    { id: 'electricity', label: language === 'en' ? 'Electricity' : '電費' },
    { id: 'gas', label: language === 'en' ? 'Gas' : '瓦斯費' },
    { id: 'internet', label: language === 'en' ? 'Internet' : '網路費' },
    { id: 'cable', label: language === 'en' ? 'Cable TV' : '有線電視' },
    { id: 'heating', label: language === 'en' ? 'Heating' : '暖氣費' },
    { id: 'property_management', label: language === 'en' ? 'Property Management' : '物業管理費' },
    { id: 'other', label: language === 'en' ? 'Other' : '其他' },
  ];

  const depositType = form.watch('deposit_type');

  return (
    <>
      <FormField
        control={form.control}
        name="rent"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-lg font-semibold">
              {language === 'en' ? 'Monthly Rent (Please enter in local currency)' : '月租金（請填入當地貨幣）'}
              <span className="text-red-500 ml-1">*</span>
            </FormLabel>
            <FormControl>
              <Input 
                {...field} 
                onChange={(e) => {
                  field.onChange(e);
                  validateNumber(e.target.value, setRentError);
                }}
              />
            </FormControl>
            {rentError && <p className="text-sm font-medium text-destructive">{rentError}</p>}
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="space-y-4">
        <h3 className="text-lg font-semibold">
          {language === 'en' ? 'Utilities Included' : '租金包含費用'}
        </h3>
        
        <FormField
          control={form.control}
          name="utilities_included"
          render={({ field }) => (
            <FormItem>
              <CheckboxGroup
                items={utilitiesOptions}
                value={field.value || []}
                onChange={field.onChange}
              />
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="deposit_type"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-base font-medium">
                {language === 'en' ? 'Deposit Type' : '押金類型'}
                <span className="text-red-500 ml-1">*</span>
              </FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={language === 'en' ? "Select type" : "選擇類型"} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="amount">
                    {language === 'en' ? 'Fixed Amount' : '固定金額'}
                  </SelectItem>
                  <SelectItem value="months">
                    {language === 'en' ? 'Number of Months' : '月數'}
                  </SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="deposit_value"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-base font-medium">
                {depositType === 'months' 
                  ? (language === 'en' ? 'Deposit Months' : '押金月數')
                  : (language === 'en' ? 'Deposit Amount' : '押金金額')
                }
                <span className="text-red-500 ml-1">*</span>
              </FormLabel>
              <FormControl>
                <Input 
                  {...field} 
                  onChange={(e) => {
                    field.onChange(e);
                    validateNumber(e.target.value, setDepositError);
                  }}
                />
              </FormControl>
              {depositError && <p className="text-sm font-medium text-destructive">{depositError}</p>}
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </>
  );
};

export const OptionsFields = ({ form }: { form: any }) => {
  const { language } = useLanguage();

  return (
    <>
      <FormField
        control={form.control}
        name="min_rental_period"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-base font-medium">
              {language === 'en' ? 'Minimum Rental Period (Months)' : '最短租期（月）'}
            </FormLabel>
            <FormControl>
              <Input type="number" placeholder="3" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="notice_period"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-base font-medium">
              {language === 'en' ? 'Notice Period (Days)' : '提前通知期限（天）'}
            </FormLabel>
            <FormControl>
              <Input type="number" placeholder="30" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="viewing_during_lease"
        render={({ field }) => (
          <FormItem className="flex flex-row items-center justify-between rounded-md border p-4">
            <div className="space-y-0.5">
              <FormLabel className="text-base font-medium">
                {language === 'en' ? 'Allow Viewing During Lease' : '允許在租賃期間看房'}
              </FormLabel>
            </div>
            <FormControl>
              <Checkbox
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            </FormControl>
          </FormItem>
        )}
      />
    </>
  );
};

export const ImmigrantFriendlyFields = ({ form }: { form: any }) => {
  const { language } = useLanguage();

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium">
        {language === 'en' ? 'Immigrant Friendly Level' : '對新移民友善程度'}
      </h3>
      
      <div className="grid grid-cols-1 gap-4">
        <FormField
          control={form.control}
          name="accepts_overseas_proof_of_funds"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel className="text-sm font-normal">
                  {language === 'en' ? 'Accepts overseas proof of funds' : '接受海外資金證明'}
                </FormLabel>
              </div>
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="accepts_overseas_employment_proof"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel className="text-sm font-normal">
                  {language === 'en' ? 'Accepts overseas employment proof' : '接受海外工作證明'}
                </FormLabel>
              </div>
            </FormItem>
          )}
        />
      </div>
    </div>
  );
};

export const PetFriendlyFields = ({ form }: { form: any }) => {
  const { language } = useLanguage();

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium">
        {language === 'en' ? 'Pet Friendly Level' : '寵物友善程度'}
      </h3>
      
      <div className="grid grid-cols-1 gap-4">
        <FormField
          control={form.control}
          name="pet_allowed"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel className="text-sm font-normal">
                  {language === 'en' ? 'Pets allowed' : '允許養寵物'}
                </FormLabel>
              </div>
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="pet_fee"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel className="text-sm font-normal">
                  {language === 'en' ? 'Pet fee required' : '需要寵物費用'}
                </FormLabel>
              </div>
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="no_pet_limit_count"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel className="text-sm font-normal">
                  {language === 'en' ? 'No limit on pet count' : '不限寵物數量'}
                </FormLabel>
              </div>
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="no_pet_limit_type"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel className="text-sm font-normal">
                  {language === 'en' ? 'No limit on pet type' : '不限寵物種類'}
                </FormLabel>
              </div>
            </FormItem>
          )}
        />
      </div>
    </div>
  );
};

export const ApplicationEaseFields = ({ form }: { form: any }) => {
  const { language } = useLanguage();

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium">
        {language === 'en' ? 'Application Ease Level' : '申請容易程度'}
      </h3>
      
      <div className="grid grid-cols-1 gap-4">
        <FormField
          control={form.control}
          name="requires_employment_proof"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel className="text-sm font-normal">
                  {language === 'en' ? 'Requires employment proof' : '需要工作證明'}
                </FormLabel>
              </div>
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="requires_credit_check"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel className="text-sm font-normal">
                  {language === 'en' ? 'Requires credit check' : '需要信用檢查'}
                </FormLabel>
              </div>
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="has_income_requirement"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel className="text-sm font-normal">
                  {language === 'en' ? 'Has income requirement' : '有收入要求'}
                </FormLabel>
              </div>
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="requires_financial_proof"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel className="text-sm font-normal">
                  {language === 'en' ? 'Requires financial proof' : '需要財力證明'}
                </FormLabel>
              </div>
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="no_proof_required"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel className="text-sm font-normal">
                  {language === 'en' ? 'No proof required' : '無需提供任何證明'}
                </FormLabel>
              </div>
            </FormItem>
          )}
        />
      </div>
    </div>
  );
};

export const ContractFlexibilityFields = ({ form }: { form: any }) => {
  const { language } = useLanguage();

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium">
        {language === 'en' ? 'Contract Flexibility Level' : '合約彈性程度'}
      </h3>
      
      <div className="grid grid-cols-1 gap-4">
        <FormField
          control={form.control}
          name="allows_daily_rental"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel className="text-sm font-normal">
                  {language === 'en' ? 'Allows daily rental' : '允許日租'}
                </FormLabel>
              </div>
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="allows_monthly_rental"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel className="text-sm font-normal">
                  {language === 'en' ? 'Allows monthly rental' : '允許月租'}
                </FormLabel>
              </div>
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="allows_semiannual_rental"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel className="text-sm font-normal">
                  {language === 'en' ? 'Allows semi-annual rental' : '允許半年租'}
                </FormLabel>
              </div>
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="allows_contract_termination"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel className="text-sm font-normal">
                  {language === 'en' ? 'Allows contract termination' : '允許中途解約'}
                </FormLabel>
              </div>
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="allows_contract_transfer"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel className="text-sm font-normal">
                  {language === 'en' ? 'Allows contract transfer' : '允許轉讓合約'}
                </FormLabel>
              </div>
            </FormItem>
          )}
        />
      </div>
      
      {/* Parking Information */}
      <div className="space-y-4 mt-6">
        <h4 className="text-md font-medium">
          {language === 'en' ? 'Parking Information' : '停車資訊'}
        </h4>
        
        <FormField
          control={form.control}
          name="parking_information"
          render={({ field }) => (
            <FormItem>
              <div className="grid grid-cols-1 gap-4">
                {[
                  { value: 'free_indoor', label: language === 'en' ? 'Free indoor parking' : '免費室內停車' },
                  { value: 'free_outdoor', label: language === 'en' ? 'Free outdoor parking' : '免費戶外停車' },
                  { value: 'paid_indoor', label: language === 'en' ? 'Paid indoor parking' : '付費室內停車' },
                  { value: 'paid_outdoor', label: language === 'en' ? 'Paid outdoor parking' : '付費戶外停車' },
                ].map((option) => (
                  <div key={option.value} className="flex flex-row items-start space-x-3 space-y-0">
                    <Checkbox
                      checked={field.value?.includes(option.value) || false}
                      onCheckedChange={(checked) => {
                        const currentValue = field.value || [];
                        if (checked) {
                          field.onChange([...currentValue, option.value]);
                        } else {
                          field.onChange(currentValue.filter((v: string) => v !== option.value));
                        }
                      }}
                    />
                    <div className="space-y-1 leading-none">
                      <label className="text-sm font-normal">
                        {option.label}
                      </label>
                    </div>
                  </div>
                ))}
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
};
